// Reviewing part of a block: which rows a line selection names, which
// lines marking or unmarking it writes, and how covered rows are shown.
// Which lines the marks cover is the review model's rule alone
// (`review/model.ts`, docs/design.md "Review marks"); everything here works
// from the coverage it gives a block's rows.
import type { Anchor, Block, FileSides, Row, Side as MarkSide } from '../engine/types';
import type { Line } from '../review/anchors';
import { changedLine, isUnreviewed } from '../review/model';
import { anchorKey, type LineCursor } from '../state/anchor';

type Side = 'LEFT' | 'RIGHT';

/** A removed or added row (`changedLine`). */
const changed = (r: Row) => changedLine(r) !== null;

/** The line a row shows on `side`, or null (a removed row has no new line, an added row no old one). */
function lineOn(r: Row, side: Side): number | null {
  return side === 'LEFT' ? (r.kind !== 'add' ? r.old : null) : r.kind !== 'del' ? r.new : null;
}

const markLine = changedLine;

// ---------------------------------------------------------------- coverage

/** Which rows of one block the review marks cover. */
export interface Coverage {
  /** One per row of the block: the row's changed line is covered. Context rows never are. */
  covered: boolean[];
  /** The block has a removed or added row and every one is covered. */
  reviewed: boolean;
}

/** A block's coverage, from whether a mark covers each line (`FileReview.covers`). */
export function blockCoverage(block: Block, covers: (side: MarkSide, line: number) => boolean): Coverage {
  let any = false;
  let all = true;
  const covered = block.rows.map((r) => {
    const l = markLine(r);
    if (!l) return false;
    any = true;
    const c = covers(l.side, l.line);
    if (!c) all = false;
    return c;
  });
  return { covered, reviewed: any && all };
}

/** The changed lines of `rows` (indices) of a block, as marks name them: what marking them writes. */
export function rowLines(block: Block, rows: Iterable<number>): Line[] {
  const out: Line[] = [];
  for (const j of rows) {
    const l = block.rows[j] && markLine(block.rows[j]);
    if (l) out.push(l);
  }
  return out;
}

/** Every changed line of a block. */
export function blockMarkLines(block: Block): Line[] {
  return rowLines(block, block.rows.keys());
}

/** Whether one of `anchors` (marks not saved yet) names a line of `block` on the file's `sides`. */
export function reachesBlock(block: Block, sides: FileSides | null | undefined, anchors: readonly Anchor[]): boolean {
  if (!sides || !anchors.length) return false;
  const mine = anchors.filter((a) => {
    const at = sides[a.side];
    return !!at && at.path === a.path && at.blob === a.blob;
  });
  if (!mine.length) return false;
  return block.rows.some((r) => {
    const l = markLine(r);
    return !!l && mine.some((a) => a.side === l.side && a.startLine <= l.line && l.line <= a.endLine);
  });
}

/** Covered and total removed and added lines of one block. */
export function blockLines(block: Block, coverage: Coverage | null): { done: number; total: number } {
  let done = 0;
  let total = 0;
  block.rows.forEach((r, j) => {
    if (!changed(r)) return;
    total++;
    if (coverage?.covered[j]) done++;
  });
  return { done, total };
}

/**
 * Whether a change block is reviewed under `coverage`: none of its removed
 * and added lines is unreviewed (`isUnreviewed`). n / p, `r`, the block's
 * Reviewed checkbox and its default fold all go by this; with the coverage
 * not known (null) no line counts as covered.
 */
export function blockReviewed(block: Block, coverage: Coverage | null | undefined): boolean {
  return !isUnreviewed(blockLines(block, coverage ?? null));
}

/**
 * Where n and p put the line cursor in a block: its first removed or added
 * line no mark covers, at row `from` or after (after marking a chunk, the
 * next line to review).
 */
export function firstUncovered(block: Block, coverage: Coverage | null, from = 0): LineCursor | null {
  const j = block.rows.findIndex((r, i) => i >= from && changed(r) && !coverage?.covered[i]);
  if (j < 0) return null;
  const r = block.rows[j];
  return r.kind === 'del' ? { side: 'LEFT', line: r.old! } : { side: 'RIGHT', line: r.new! };
}

// ---------------------------------------------------------------- selection

/** The rows of one block a line selection spans. */
export interface Span {
  block: number;
  /** Row indices, half-open: from the first row showing a selected line to the last. */
  start: number;
  end: number;
  /** Removed and added rows in the span, and how many of them are covered. */
  lines: number;
  covered: number;
}

/**
 * The rows a selection of lines `a`..`b` (either order) on `side` spans in
 * each block it reaches, with the rows of the other side between them (a
 * replacement's removed lines sit between its added ones' neighbors).
 * Blocks with no removed or added row in their span are left out.
 */
export function selectionSpans(blocks: Block[], coverage: (Coverage | undefined)[], side: Side, a: number, b: number): Span[] {
  const [lo, hi] = a <= b ? [a, b] : [b, a];
  const out: Span[] = [];
  blocks.forEach((block, i) => {
    if (block.kind !== 'change') return;
    let start = -1;
    let end = -1;
    block.rows.forEach((r, j) => {
      const n = lineOn(r, side);
      if (n === null || n < lo || n > hi) return;
      if (start < 0) start = j;
      end = j + 1;
    });
    if (start < 0) return;
    let lines = 0;
    let covered = 0;
    for (let j = start; j < end; j++) {
      if (!changed(block.rows[j])) continue;
      lines++;
      if (coverage[i]?.covered[j]) covered++;
    }
    if (lines) out.push({ block: i, start, end, lines, covered });
  });
  return out;
}

/** The changed lines the spans select, as marks name them. */
export function spanLines(blocks: Block[], spans: Span[]): Line[] {
  return spans.flatMap((s) => {
    const rows: number[] = [];
    for (let j = s.start; j < s.end; j++) rows.push(j);
    return rowLines(blocks[s.block], rows);
  });
}

/** What marking says when it made changes reviewed, by their numbers: "Changes 2 and 3 reviewed". */
export function reviewedNote(numbers: number[]): string {
  if (numbers.length === 1) return `Change ${numbers[0]} reviewed`;
  const head = numbers.slice(0, -1).join(', ');
  return `Changes ${head} and ${numbers[numbers.length - 1]} reviewed`;
}

// ---------------------------------------------------------------- display

/** One cell of a displayed line: the row it shows (and its index in the block), on one side. */
export type Cell = { row: Row; index: number; side: 'old' | 'new' } | null;

/** A displayed line: in split view a removed row and its paired added row side by side. */
export interface VisualLine {
  left: Cell;
  right: Cell;
  /** The comment anchors on the line ("L:12", "R:40"). */
  anchors: string[];
}

/** A block's displayed lines in `mode`. */
export function visualLines(block: Block, mode: 'split' | 'unified'): VisualLine[] {
  const out: VisualLine[] = [];
  const rows = block.rows;
  if (mode === 'unified') {
    rows.forEach((r, index) => {
      const side = r.kind === 'del' ? 'old' : 'new';
      const anchors: string[] = [];
      if (r.old !== null && r.kind !== 'add') anchors.push(anchorKey('LEFT', r.old));
      if (r.new !== null && r.kind !== 'del') anchors.push(anchorKey('RIGHT', r.new));
      out.push({ left: { row: r, index, side }, right: null, anchors });
    });
    return out;
  }
  // Each change run in pair order: a removed row beside the added row it
  // pairs with (pairs never cross), any added rows before that one on
  // their own lines first, and every unpaired row opposite filler.
  const del = (i: number): Cell => ({ row: rows[i], index: i, side: 'old' });
  const add = (i: number): Cell => ({ row: rows[i], index: i, side: 'new' });
  const addLine = (i: number) => out.push({ left: null, right: add(i), anchors: [anchorKey('RIGHT', rows[i].new!)] });
  let i = 0;
  while (i < rows.length) {
    const r = rows[i];
    if (r.kind === 'context') {
      out.push({
        left: r.old !== null ? { row: r, index: i, side: 'old' } : null,
        right: r.new !== null ? { row: r, index: i, side: 'new' } : null,
        anchors: [...(r.old !== null ? [anchorKey('LEFT', r.old)] : []), ...(r.new !== null ? [anchorKey('RIGHT', r.new)] : [])],
      });
      i++;
      continue;
    }
    let end = i;
    while (end < rows.length && rows[end].kind !== 'context') end++;
    const dels: number[] = [];
    const adds: number[] = [];
    for (let j = i; j < end; j++) (rows[j].kind === 'del' ? dels : adds).push(j);
    let next = 0; // the first added row not yet shown
    for (const d of dels) {
      const p = rows[d].pair;
      const at = p === null ? -1 : adds.indexOf(p, next);
      if (at < 0) {
        out.push({ left: del(d), right: null, anchors: [anchorKey('LEFT', rows[d].old!)] });
        continue;
      }
      for (; next < at; next++) addLine(adds[next]);
      next = at + 1;
      out.push({ left: del(d), right: add(p!), anchors: [anchorKey('LEFT', rows[d].old!), anchorKey('RIGHT', rows[p!].new!)] });
    }
    for (; next < adds.length; next++) addLine(adds[next]);
    i = end;
  }
  return out;
}

/** A run of displayed lines whose every row is covered, folded into "✓ N lines reviewed". */
export interface Stub {
  /** Displayed line indices, half-open. */
  from: number;
  to: number;
  /** Removed and added lines folded. */
  lines: number;
}

/**
 * The covered runs of a partly reviewed block's displayed lines, as stubs:
 * at least two lines long, each line's every cell a covered removed or
 * added row. A line with inline content (a thread, a draft, an annotation)
 * is never folded away.
 */
export function coveredStubs(lines: VisualLine[], covered: readonly boolean[], hasInline: (anchors: string[]) => boolean): Stub[] {
  const out: Stub[] = [];
  let from = -1;
  let count = 0;
  const close = (to: number) => {
    if (from >= 0 && to - from >= 2) out.push({ from, to, lines: count });
    from = -1;
    count = 0;
  };
  lines.forEach((l, i) => {
    const cells = [l.left, l.right].filter((c): c is NonNullable<Cell> => c !== null);
    const folds = cells.length > 0 && cells.every((c) => changed(c.row) && covered[c.index]) && !hasInline(l.anchors);
    if (!folds) return close(i);
    if (from < 0) from = i;
    count += cells.length;
  });
  close(lines.length);
  return out;
}

/** Row indices the stubs not `expanded` hide. */
export function hiddenRows(lines: VisualLine[], stubs: Stub[], expanded: (s: Stub) => boolean): Set<number> {
  const out = new Set<number>();
  for (const s of stubs) {
    if (expanded(s)) continue;
    for (let i = s.from; i < s.to; i++) for (const c of [lines[i].left, lines[i].right]) if (c) out.add(c.index);
  }
  return out;
}
